import { cn } from '../cn.js';

export interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  /** A token reference, e.g. "var(--success)" — never a literal colour. Defaults by trend
   *  direction (last value vs first) when omitted, so a caller doesn't have to compute it twice. */
  color?: string;
  className?: string;
  /** Line weight in px. Defaults to 1.5 — the original hairline every existing caller keeps unless
   *  it opts into something heavier (e.g. a KPI card where the sparkline carries more visual
   *  weight than a small table-header aside). */
  strokeWidth?: number;
  /** Fills the area under the line with the trend colour at low, fixed opacity. No `<defs>`/
   *  gradient — same reasoning `LineChart.tsx` documents for itself — so two sparklines on one
   *  page never fight over a shared id. Off by default; matches every caller's current look. */
  area?: boolean;
}

/**
 * Inline trend line with no axes or labels — "Quarterly Growth +1.2%"'s tiny chart, or anywhere a
 * single number's recent direction matters more than its exact history. `LineChart`'s minimal
 * sibling: same hand-rolled-SVG reasoning (see `LineChart.tsx`), but sized for a KPI card rather
 * than a full panel.
 */
export function Sparkline({
  values,
  width = 64,
  height = 24,
  color,
  className,
  strokeWidth = 1.5,
  area = false,
}: SparklineProps) {
  if (values.length === 0) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = values.length > 1 ? width / (values.length - 1) : 0;

  const coords = values.map((value, i) => ({
    x: i * step,
    y: height - ((value - min) / range) * height,
  }));
  const points = coords.map((p) => `${p.x},${p.y}`).join(' ');

  const firstValue = values[0]!;
  const lastValue = values[values.length - 1]!;
  const trendColor = color ?? (lastValue >= firstValue ? 'var(--success)' : 'var(--danger)');
  const lastPoint = coords[coords.length - 1]!;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className={cn('inline-block', className)}
      role="img"
      aria-label="Trend"
    >
      {area && (
        <polygon
          points={`0,${height} ${points} ${lastPoint.x},${height}`}
          fill={trendColor}
          fillOpacity={0.15}
          stroke="none"
        />
      )}
      <polyline
        points={points}
        fill="none"
        stroke={trendColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={lastPoint.x} cy={lastPoint.y} r={2} fill={trendColor} />
    </svg>
  );
}
